import { makePathArray } from '@tanstack/react-form'
import { FetchResponse } from 'openapi-fetch'
import { ZodIssueBase } from 'zod'

import { getErrorMessageByStatusCode } from '~/hooks/tanstack-query'
import { baseErrorFormatter } from '~/hooks/tanstack-query/shared/apiWrapper'
import { dispatchErrorToast } from '~/store/slices/toasterSlice'

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export type ClientResponse = Pick<FetchResponse<any, any, any>, 'error' | 'response'>

export const dispatchFirstError = (results: ClientResponse[]): ClientResponse | undefined => {
    const allErrors = results.filter(r => !!r.error)
    const firstError = allErrors[0]

    if (allErrors.length > 0) {
        const errorMessages = allErrors.map(r => baseErrorFormatter(r.response, r.error))
        console.error('Number of errors:', allErrors.length, ':', errorMessages.join('\n'))
    }

    if (firstError) {
        dispatchErrorToast({ message: getErrorMessageByStatusCode(firstError.response.status) })
        return firstError
    }
    return undefined
}

export const isErrorWithMessage = (error: unknown): error is { message: string } =>
    typeof error === 'object' && error !== null && 'message' in error && typeof error.message === 'string' && error.message.length > 0

export const isZodError = (error: unknown): error is ZodIssueBase =>
    typeof error === 'object' && error !== null && 'message' in error && typeof error.message === 'string' && 'path' in error && Array.isArray(error.path)

const isSubPath = (prefix: (string | number)[], issuePath: ZodIssueBase['path']) => prefix.every((segment, i) => issuePath[i] === segment)

export const getNestedFieldErrors = (errors: unknown[], path: string): ZodIssueBase[] => {
    const pathArray = makePathArray(path)
    const zodIssues = errors.flatMap(entry => {
        if (isZodError(entry)) {
            return entry
        }
        if (entry && typeof entry === 'object') {
            return Object.values(entry).flat().filter(isZodError)
        }
        return []
    })

    return zodIssues.filter(issue => isSubPath(pathArray, issue.path))
}
